import React, { useCallback, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GlobeIcon, XIcon, SearchIcon, Loader2Icon, CheckIcon, ChevronDownIcon, CheckCircleIcon, ThumbsUpIcon, ThumbsDownIcon } from 'lucide-react';
import { SearchResultItem } from './SearchResultItem';
export type SearchResultType = 'article' | 'video' | 'paper' | 'pdf';
export interface SearchResult {
  id: string;
  title: string;
  url: string;
  domain?: string;
  /** Optional type label displayed next to the URL */
  type?: SearchResultType;
}
export interface WebSearchModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Called when the modal should close */
  onClose: () => void;
  /** Called when the user initiates a search */
  onSearch: (query: string) => void;
  /** Called when the user imports selected results */
  onImport: (selectedResults: SearchResult[]) => void;
  /** Search results to display */
  results: SearchResult[];
  /** Whether a search is in progress */
  isSearching?: boolean;
  /** Set of result IDs that have already been imported */
  importedIds?: Set<string>;
  /** Number of results to show before "Show more" (default: 5) */
  initialVisibleCount?: number;
  /** Called when user gives thumbs up/down feedback on the search results */
  onFeedback?: (feedback: 'up' | 'down') => void;
  /** Optional data-id for identification */
  'data-id'?: string;
}
const DEFAULT_VISIBLE_COUNT = 5;
export function WebSearchModal({
  isOpen,
  onClose,
  onSearch,
  onImport,
  results,
  isSearching = false,
  importedIds = new Set(),
  initialVisibleCount = DEFAULT_VISIBLE_COUNT,
  onFeedback,
  'data-id': dataId
}: WebSearchModalProps) {
  const [query, setQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchFeedback, setSearchFeedback] = useState<'up' | 'down' | null>(null);
  const visibleResults = useMemo(() => showAll ? results : results.slice(0, initialVisibleCount), [results, showAll, initialVisibleCount]);
  const hiddenCount = results.length - initialVisibleCount;
  const handleSearch = useCallback((q: string) => {
    if (!q.trim() || isSearching) return;
    setHasSearched(true);
    setSelectedIds(new Set());
    setShowAll(false);
    setSearchFeedback(null);
    onSearch(q.trim());
  }, [isSearching, onSearch]);
  const toggleSelection = useCallback((id: string) => {
    if (importedIds.has(id)) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, [importedIds]);
  const toggleAll = useCallback(() => {
    const selectableResults = results.filter(r => !importedIds.has(r.id));
    const allSelected = selectableResults.every(r => selectedIds.has(r.id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableResults.map(r => r.id)));
    }
  }, [results, importedIds, selectedIds]);
  const handleImport = useCallback(() => {
    if (selectedIds.size === 0) return;
    const selected = results.filter(r => selectedIds.has(r.id));
    onImport(selected);
    setSelectedIds(new Set());
  }, [selectedIds, results, onImport]);
  const selectableResults = useMemo(() => results.filter(r => !importedIds.has(r.id)), [results, importedIds]);
  const allSelectableSelected = selectableResults.length > 0 && selectableResults.every(r => selectedIds.has(r.id));
  return <AnimatePresence data-id="element-1189">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id={dataId}>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" data-id="element-1190" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 12
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 12
      }} transition={{
        duration: 0.2
      }} className="relative w-full max-w-2xl bg-surface rounded-2xl shadow-2xl flex flex-col max-h-[85vh] overflow-hidden" role="dialog" aria-modal="true" aria-label="Web Search" data-id="element-1191">
            {/* ─── Header ─── */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-custom flex-shrink-0" data-id="element-1192">
              <div className="flex items-center gap-2.5" data-id="element-1193">
                <div className="w-8 h-8 rounded-lg bg-[#2563EB]/10 flex items-center justify-center" data-id="element-1194">
                  <GlobeIcon size={16} className="text-[#2563EB]" data-id="element-1195" />
                </div>
                <h2 className="text-sm font-bold text-text-primary" data-id="element-1196">
                  Web Search
                </h2>
              </div>
              <button onClick={onClose} className="p-2 -mr-1 rounded-lg hover:bg-surface-elevated text-text-tertiary hover:text-text-primary transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Close search modal" data-id="element-1197">
                <XIcon size={18} data-id="element-1198" />
              </button>
            </div>

            {/* ─── Search Bar ─── */}
            <div className="px-5 py-3 border-b border-custom flex-shrink-0" data-id="element-1199">
              <div className="relative flex items-center" data-id="element-1200">
                <SearchIcon size={16} className="absolute left-3.5 text-text-tertiary pointer-events-none" data-id="element-1201" />
                <input type="text" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter') handleSearch(query);
            }} placeholder="Search for a topic or resource..." className="w-full h-10 pl-10 pr-24 bg-surface-elevated border border-custom rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent text-text-primary placeholder-text-tertiary" autoFocus data-id="element-1202" />
                <button onClick={() => handleSearch(query)} disabled={!query.trim() || isSearching} className={`absolute right-1 px-4 h-8 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5 ${query.trim() && !isSearching ? 'bg-[#00B75F] text-white hover:bg-[#00a055]' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-1203">
                  {isSearching ? <Loader2Icon size={13} className="animate-spin" data-id="element-1204" /> : <>
                      <SearchIcon size={12} data-id="element-1205" />
                      Search
                    </>}
                </button>
              </div>
            </div>

            {/* ─── Content Area ─── */}
            <div className="flex-1 overflow-y-auto" data-id="element-1206">
              <div className="p-4" data-id="element-1207">
                {/* Loading */}
                {isSearching ? <div className="space-y-2" data-id="element-1208">
                    {[1, 2, 3, 4, 5].map(i => <div key={i} className="bg-surface border border-custom rounded-lg p-2.5 animate-pulse flex items-center gap-3" data-id="element-1209">
                        <div className="w-4 h-4 rounded border border-gray-200 bg-gray-100 flex-shrink-0" data-id="element-1210" />
                        <div className="flex-1 space-y-1.5" data-id="element-1211">
                          <div className="h-3.5 bg-gray-200 rounded w-3/4" data-id="element-1212" />
                          <div className="h-3 bg-gray-100 rounded w-1/3" data-id="element-1213" />
                        </div>
                      </div>)}
                  </div> : results.length > 0 ? <div className="space-y-1.5" data-id="element-1214">
                    {/* Results header with Select All */}
                    <div className="flex items-center gap-2.5 mb-2 px-0.5" data-id="element-1215">
                      <button onClick={toggleAll} className="flex items-center gap-2 group" data-id="element-1216">
                        <div className={`w-4 h-4 rounded flex items-center justify-center border transition-colors ${allSelectableSelected ? 'bg-gray-700 border-gray-700 text-white' : 'border-gray-300 bg-surface group-hover:border-gray-400'}`} data-id="element-1217">
                          {allSelectableSelected && <CheckIcon size={11} strokeWidth={3} data-id="element-1218" />}
                        </div>
                        <span className="text-xs font-medium text-text-secondary group-hover:text-text-primary transition-colors" data-id="element-1219">
                          {allSelectableSelected ? 'Deselect all' : 'Select all'}
                        </span>
                      </button>
                      <span className="text-[11px] text-text-tertiary ml-auto" data-id="element-1220">
                        {results.length} results
                      </span>
                    </div>

                    {/* Result rows */}
                    {visibleResults.map((result, index) => <SearchResultItem key={result.id} result={result} index={index} isSelected={selectedIds.has(result.id)} isAlreadyImported={importedIds.has(result.id)} onToggle={toggleSelection} data-id="element-1221" />)}

                    {/* Show more */}
                    {!showAll && hiddenCount > 0 && <button onClick={() => setShowAll(true)} className="w-full py-2 mt-1 flex items-center justify-center gap-1.5 text-xs font-medium text-[#2563EB] hover:text-[#1d4ed8] hover:bg-[#2563EB]/[0.04] rounded-lg transition-colors" data-id="element-1222">
                        <ChevronDownIcon size={13} data-id="element-1223" />
                        Show {hiddenCount} more
                      </button>}
                    {showAll && results.length > initialVisibleCount && <button onClick={() => setShowAll(false)} className="w-full py-2 mt-1 flex items-center justify-center text-[11px] font-medium text-text-tertiary hover:text-text-secondary transition-colors" data-id="element-1224">
                        Show less
                      </button>}
                  </div> : hasSearched && !isSearching /* No results */ ? <div className="flex flex-col items-center justify-center text-center py-12" data-id="element-1225">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3" data-id="element-1226">
                      <SearchIcon size={20} className="text-gray-400" data-id="element-1227" />
                    </div>
                    <h3 className="text-sm font-semibold text-text-primary mb-1" data-id="element-1228">
                      No results found
                    </h3>
                    <p className="text-xs text-text-secondary max-w-xs" data-id="element-1229">
                      Try different keywords or broaden your search.
                    </p>
                  </div> /* Empty / initial state */ : <div className="flex flex-col items-center justify-center text-center py-12" data-id="element-1230">
                    <div className="w-12 h-12 bg-[#2563EB]/5 rounded-full flex items-center justify-center mb-3" data-id="element-1231">
                      <SearchIcon size={20} className="text-[#2563EB]/40" data-id="element-1232" />
                    </div>
                    <p className="text-sm font-medium text-text-secondary" data-id="element-1233">
                      Search for a topic
                    </p>
                    <p className="text-xs text-text-tertiary mt-0.5" data-id="element-1234">
                      Find articles, videos, papers, and more.
                    </p>
                  </div>}
              </div>
            </div>

            {/* ─── Footer ─── */}
            <div className="px-5 py-3 border-t border-custom bg-surface flex items-center justify-between flex-shrink-0" data-id="element-1235">
              {/* Left: feedback + selected count */}
              <div className="flex items-center gap-3" data-id="element-1236">
                {results.length > 0 && <div className="flex items-center gap-1" data-id="element-1237">
                    <span className="text-[11px] text-text-tertiary mr-0.5" data-id="element-1238">
                      Results helpful?
                    </span>
                    <button onClick={() => {
                const val = searchFeedback === 'up' ? null : 'up';
                setSearchFeedback(val);
                if (val && onFeedback) onFeedback('up');
              }} className={`p-1 rounded transition-colors ${searchFeedback === 'up' ? 'text-[#00B75F] bg-[#00B75F]/10' : 'text-text-tertiary hover:text-text-secondary hover:bg-surface-elevated'}`} aria-label="Results were helpful" data-id="element-1239">
                      <ThumbsUpIcon size={13} strokeWidth={searchFeedback === 'up' ? 2.5 : 2} data-id="element-1240" />
                    </button>
                    <button onClick={() => {
                const val = searchFeedback === 'down' ? null : 'down';
                setSearchFeedback(val);
                if (val && onFeedback) onFeedback('down');
              }} className={`p-1 rounded transition-colors ${searchFeedback === 'down' ? 'text-rose-500 bg-rose-500/10' : 'text-text-tertiary hover:text-text-secondary hover:bg-surface-elevated'}`} aria-label="Results were not helpful" data-id="element-1241">
                      <ThumbsDownIcon size={13} strokeWidth={searchFeedback === 'down' ? 2.5 : 2} data-id="element-1242" />
                    </button>
                  </div>}
                {results.length > 0 && selectedIds.size > 0 && <span className="text-[11px] font-medium text-text-tertiary" data-id="element-1243">
                    {selectedIds.size} selected
                  </span>}
              </div>

              {/* Right: actions */}
              <div className="flex items-center gap-2" data-id="element-1244">
                <button onClick={onClose} className="px-3.5 py-1.5 rounded-lg text-xs font-medium text-text-secondary hover:bg-surface-elevated transition-colors min-h-[36px]" data-id="element-1245">
                  Cancel
                </button>
                <button onClick={handleImport} disabled={selectedIds.size === 0} className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 min-h-[36px] ${selectedIds.size > 0 ? 'bg-[#00B75F] text-white hover:bg-[#00a055]' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-1246">
                  <CheckCircleIcon size={13} data-id="element-1247" />
                  Import{selectedIds.size > 0 ? ` ${selectedIds.size}` : ''}
                </button>
              </div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}